// Kontener zarządzania produktami w ofercie handlowca - podpięty pod realne endpointy backend SalesCRM.

import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Checkbox, CircularProgress, Menu, MenuItem, Pagination, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  WarehouseIcon,
  WalletIcon,
  DiscountIcon,
  PlusCircleIcon,
  AttachmentIcon
} from 'assets/icons';

import {
  IPriceThreshold,
  ISalesCrmOfferCalculationPosition,
  ISalesCrmOfferPosition,
  ISalesCrmOfferPositionStock,
  ISalesCrmOfferPositionUpsellProduct,
  getSalesCrmOfferCalculationPositionsQueryKey,
  postSalesCrmOfferCalculationPosition,
  useDeleteSalesCrmOfferPosition,
  useGetSalesCrmOfferPositions,
  usePostSalesCrmOfferCalculation,
  usePutSalesCrmOfferPosition
} from 'api';
import { useQueryClient } from 'react-query';

import AddOfferPositionModal from './AddOfferPositionModal';
import EditOfferPositionUnitModal from './EditOfferPositionUnitModal';
import BulkSetDiscountModal from './BulkSetDiscountModal';
import AddPositionCommentModal from './AddPositionCommentModal';
import PositionCommentsCard from './PositionCommentsCard';
import PositionUpsaleServicesList from './PositionUpsaleServicesList';
import PriceThresholdsEditor from './PriceThresholdsEditor';
import ChooseCalculationActionModal from './ChooseCalculationActionModal';
import ChooseCalculationTemplateModal from './ChooseCalculationTemplateModal';
import CalculationPositionsModal from './CalculationPositionsModal';
import AddCalculationPositionModal from './AddCalculationPositionModal';
import CreateCalculationModal from './CreateCalculationModal';
import LinkCalculationModal from './LinkCalculationModal';
import PositionCalculationSummary from './PositionCalculationSummary';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/TraderClientOfferProductsContainer.module.scss';

const PAGE_LIMIT = 10;
const BACKEND_FETCH_LIMIT = 9999;

interface ITraderClientOfferProductsContainerProps {
  clientId: number;
  offerId: string;
  /** Wymuszona wersja oferty (null => aktualna). */
  versionId?: number | null;
  /** Czy oferta jest w stanie DRAFT — kontroluje widoczność akcji usług per-pozycja. */
  isOfferDraft?: boolean;
}

interface IRenderPosition {
  id: number;
  lp: number;
  productId: number | null;
  name: string;
  symbol: string;
  category: string;
  tags: string[];
  imageUrl: string | null;
  warehouseId: number | null;
  stocks: ISalesCrmOfferPositionStock[];
  qty: number;
  qtyMin: number;
  /** Jednostka miary z głównej ceny produktu (np. "Sztuka", "Karton", "kg"). Fallback "szt." gdy backend nie zwraca. */
  unit: string;
  /** Id jednostki produktu (ProductUnit) - uzywane do edycji jednostki pozycji. */
  productUnitId: number | null;
  priceCatalog: string;
  priceDiscount: string;
  priceSale: string;
  discountPct: number;
  discountValue: number;
  priceGross: string;
  hasComment: boolean;
  hasAttachment: boolean;
  purchaseCostNet: number | null;
  minSalesNet: number | null;
  marginNet: number | null;
  marginNetTotal: number | null;
  ownCostsNet: number | null;
  upsellProducts: ISalesCrmOfferPositionUpsellProduct[];
  /** Raw cena netto sprzedażowa — używana przez `PriceThresholdsEditor` jako default nowego progu. */
  priceNetSalesRaw: number;
  /** Raw cena brutto sprzedażowa — używana m.in. do automatycznego dodania pierwszej pozycji nowej kalkulacji. */
  priceGrossSalesRaw: number;
  /** Lista progów cenowych (brak/null = brak progów). */
  priceThresholds: IPriceThreshold[] | null;
  /** Tryb progów: false = progi ilościowe, true = warianty do wyboru przez klienta. */
  priceThresholdsAsVariants: boolean;
  /** Kalkulacja przypisana do pozycji (relacja 1:1 w UI). `null` = brak kalkulacji. */
  calculation: { id: number; name: string } | null;
}

const formatPrice = (raw?: number | null): string => {
  if (raw == null || Number.isNaN(raw)) return '0,00 zł';
  return `${raw.toFixed(2).replace('.', ',')} zł`;
};

/**
 * Czy pozycja ma "realne" progi cenowe sterujące ceną sprzedażową.
 *
 * Pojedynczy próg `qtyFrom=1` oznacza po prostu cenę bazową bez rabatu ilościowego,
 * więc rabaty handlowca pozostają edytowalne. Więcej niż jeden próg albo jeden próg
 * z `qtyFrom > 1` to faktyczne progi — wtedy rabaty są wyłączone (cena sterowana progami).
 */
const hasRealThresholds = (
  thresholds: IPriceThreshold[] | null | undefined
): boolean => {
  if (!thresholds || thresholds.length === 0) return false;
  if (thresholds.length > 1) return true;
  return thresholds[0].qtyFrom > 1;
};

/**
 * Czy w kolumnie Ilość renderujemy select wyboru wariantu (qtyFrom z progów)
 * zamiast inputa swobodnej ilości. Aktywne gdy `priceThresholdsAsVariants=true`
 * i są jakieś progi do wyboru.
 */
const isVariantsModeActive = (
  thresholds: IPriceThreshold[] | null | undefined,
  asVariants: boolean
): boolean => asVariants && Array.isArray(thresholds) && thresholds.length > 0;

/**
 * Zdjecie produktu z fallbackiem na ikone placeholder gdy:
 * - brak URL (null/empty),
 * - pobranie zwroci blad (404/inny).
 * Wzorzec z WiseProductItem (onError + state).
 */
const ProductImage: React.FC<{ src: string | null; className: string }> = ({ src, className }) => {
  const [hasError, setHasError] = useState(false);
  if (!src || hasError) {
    return (
      <div className={className}>
        <ImageOutlinedIcon fontSize="inherit" />
      </div>
    );
  }
  return (
    <div className={className}>
      <img src={src} alt="" onError={() => setHasError(true)} />
    </div>
  );
};

const TraderClientOfferProductsContainer: React.FC<ITraderClientOfferProductsContainerProps> = ({
  clientId,
  offerId,
  versionId = null,
  isOfferDraft = false
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  // Przechowuje ID pozycji, dla których user RĘCZNIE przełączył sekcję progów
  // względem domyślnego stanu (default: rozwinięte gdy są progi, zwinięte gdy ich nie ma).
  const [thresholdsUserToggledIds, setThresholdsUserToggledIds] = useState<Set<number>>(
    new Set()
  );
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [isAddPositionOpen, setIsAddPositionOpen] = useState(false);
  const [editingUnitPositionId, setEditingUnitPositionId] = useState<number | null>(null);
  const [upsellSelection, setUpsellSelection] = useState<{
    productId: number;
    searchKeyword: string;
  } | null>(null);
  const [commentForPositionId, setCommentForPositionId] = useState<number | null>(null);

  // ====== Calculation flow state machine (krok 1 → 2 → 3) ======
  type CalcModalState =
    | { kind: 'closed' }
    | { kind: 'chooseAction'; positionId: number; productName: string }
    | {
        kind: 'createForm';
        positionId: number;
        productName: string;
        submittedDescription?: string;
      }
    | { kind: 'chooseTemplate'; positionId: number; productName: string }
    | { kind: 'link'; positionId: number; productName: string }
    | {
        kind: 'positions';
        positionId: number;
        productName: string;
        calculationId: number;
        calculationName: string;
        calculationDescription: string;
      }
    | {
        kind: 'addPosition';
        positionId: number;
        productName: string;
        calculationId: number;
        calculationName: string;
        calculationDescription: string;
      }
    | {
        kind: 'editPosition';
        positionId: number;
        productName: string;
        calculationId: number;
        calculationName: string;
        calculationDescription: string;
        editPosition: ISalesCrmOfferCalculationPosition;
      };
  const [calcModal, setCalcModal] = useState<CalcModalState>({ kind: 'closed' });

  // Hook do tworzenia kalkulacji — `positionId` zmienia się dynamicznie z state machine.
  // Hook zawsze zarejestrowany; argumenty czytane przez closure w `mutate`.
  const calcModalPositionId =
    calcModal.kind === 'createForm' ? calcModal.positionId : 0;
  const { mutate: createCalculation, isLoading: isCreatingCalculation } =
    usePostSalesCrmOfferCalculation(
    clientId,
    offerId,
    calcModalPositionId,
    {
      onSuccess: async (response) => {
        if (calcModal.kind !== 'createForm') return;
        // Backend zwraca utworzoną encję w `response.data` (ICommandResponseSuccess)
        const responseData = (response.data ?? {}) as {
          id?: number;
          name?: string;
        };
        const calcId = responseData.id ?? 0;
        const calcName = responseData.name ?? calcModal.productName;
        if (calcId <= 0) return;

        // Inwalidacja listy pozycji — pozycja zyska `calculation` i przy następnym
        // renderze link pokaże "Pokaż kalkulację" zamiast "Dodaj kalkulację".
        invalidate();

        // Auto-dodajemy pierwszą pozycję kalkulacji na podstawie produktu źródłowego,
        // żeby user od razu widział pozycję w nowo utworzonej kalkulacji.
        const source = positions.find((pos) => pos.id === calcModal.positionId);
        if (source) {
          const priceNet = source.priceNetSalesRaw;
          const priceGross = source.priceGrossSalesRaw;
          const taxPercent =
            priceNet > 0 && priceGross > 0
              ? Math.round(((priceGross / priceNet) - 1) * 10000) / 100
              : 23;
          try {
            await postSalesCrmOfferCalculationPosition(clientId, offerId, calcId, {
              name: source.name,
              productId: source.productId,
              serviceId: null,
              priceType: 'UNIT',
              priceNet,
              priceGross,
              taxPercent
            });
            queryClient.invalidateQueries(
              getSalesCrmOfferCalculationPositionsQueryKey(calcId)
            );
          } catch {
            // Auto-dodanie jest udogodnieniem — w razie błędu po prostu otwieramy pusty modal pozycji.
          }
        }

        setCalcModal({
          kind: 'positions',
          positionId: calcModal.positionId,
          productName: calcModal.productName,
          calculationId: calcId,
          calculationName: calcName,
          calculationDescription: calcModal.submittedDescription ?? ''
        });
      }
    }
  );

  const handleOpenCreateForm = () => {
    if (calcModal.kind !== 'chooseAction') return;
    setCalcModal({
      kind: 'createForm',
      positionId: calcModal.positionId,
      productName: calcModal.productName
    });
  };

  const handleCreateCalculationSubmit = (values: {
    name: string;
    quantity: number;
    description: string;
  }) => {
    if (calcModal.kind !== 'createForm') return;
    setCalcModal({
      ...calcModal,
      submittedDescription: values.description
    });
    createCalculation({
      name: values.name,
      quantity: values.quantity,
      description: values.description || undefined
    });
  };
  const [rowMenuAnchor, setRowMenuAnchor] = useState<HTMLElement | null>(null);
  const [rowMenuPositionId, setRowMenuPositionId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<HTMLElement | null>(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Wybor trybu rabatu per-pozycja: 'value' (zl) | 'pct' (%). Tryb domyslny
  // wyznaczamy z danych (discountValue > 0 wygrywa, bo backend tak licza), ale
  // handlowiec moze przelaczyc - wtedy "drugi" rabat jest zerowany w PUT.
  const [discountModeOverrides, setDiscountModeOverrides] = useState<
    Record<number, 'value' | 'pct'>
  >({});
  const getDiscountMode = (positionId: number, pct: number, value: number): 'value' | 'pct' => {
    if (discountModeOverrides[positionId]) return discountModeOverrides[positionId];
    if (value > 0) return 'value';
    if (pct > 0) return 'pct';
    return 'value';
  };
  const switchDiscountMode = (positionId: number, next: 'value' | 'pct') => {
    setDiscountModeOverrides((prev) => ({ ...prev, [positionId]: next }));
    // Zerujemy "drugi" rabat zeby backend nie probowal go uzyc.
    // Wysylamy 0 (a nie null), bo PUT po stronie BE pomija pola === null
    // ("nie zmieniaj") - tylko jawne 0 nadpisze stara wartosc w bazie.
    updatePosition({
      positionId,
      payload: next === 'pct' ? { discountValue: 0 } : { discountPct: 0 }
    });
  };

  // Pobieramy wszystkie pozycje raz (do 9999) - paginacja i filtrowanie po stronie klienta.
  const { data, isLoading, isError } = useGetSalesCrmOfferPositions(
    clientId,
    offerId,
    { page: 1, limit: BACKEND_FETCH_LIMIT, versionId }
  );

  const invalidate = () =>
    queryClient.invalidateQueries(['sales-crm', 'client', clientId, 'offer', offerId], {
      refetchActive: true,
      refetchInactive: false
    });

  const {
    mutate: deletePosition,
    mutateAsync: deletePositionAsync,
    isLoading: isDeleting
  } = useDeleteSalesCrmOfferPosition(clientId, offerId, { onSuccess: invalidate });

  const { mutate: updatePosition, mutateAsync: updatePositionAsync } = usePutSalesCrmOfferPosition(
    clientId,
    offerId,
    { onSuccess: invalidate }
  );

  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isBulkDiscountModalOpen, setIsBulkDiscountModalOpen] = useState(false);

  const positions: IRenderPosition[] = useMemo(() => {
    const items = data?.items ?? [];
    return items.map((p: ISalesCrmOfferPosition, index: number): IRenderPosition => ({
      id: p.id,
      lp: index + 1,
      productId: p.productId,
      name: p.productName ?? `Produkt #${p.productId ?? '?'}`,
      symbol: p.symbol ?? '',
      category: p.category ?? '',
      tags: p.tags ?? [],
      imageUrl: p.imageUrl ?? null,
      warehouseId: p.warehouseId,
      stocks: p.stocks ?? [],
      qty: p.quantity ?? 0,
      qtyMin: p.quantityMultiplication ?? 1,
      unit: p.unit && p.unit.trim() !== '' ? p.unit : t('szt.'),
      productUnitId: p.productUnitId,
      priceCatalog: formatPrice(p.priceNetCatalog),
      priceDiscount: formatPrice(p.priceNetList),
      priceSale: formatPrice(p.priceNetSales),
      discountPct: p.discountPct ?? 0,
      discountValue: p.discountValue ?? 0,
      priceGross: formatPrice(p.priceGrossSales),
      hasComment: false,
      hasAttachment: false,
      purchaseCostNet: p.purchaseCostNet,
      minSalesNet: p.minSalesNet,
      marginNet: p.marginNet,
      marginNetTotal: p.marginNetTotal,
      ownCostsNet: p.ownCostsNet,
      upsellProducts: p.upsellProducts ?? [],
      priceNetSalesRaw: p.priceNetSales ?? 0,
      priceGrossSalesRaw: p.priceGrossSales ?? 0,
      priceThresholds: p.priceThresholds ?? null,
      priceThresholdsAsVariants: p.priceThresholdsAsVariants ?? false,
      calculation: p.calculation ?? null
    }));
  }, [data, t]);

  // Filtrowanie po stronie klienta: nazwa, symbol (kod/SKU), kategoria, tagi.
  const filteredPositions = useMemo(() => {
    if (!searchValue.trim()) return positions;
    const q = searchValue.toLowerCase();
    return positions.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.symbol.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.tags.some((tag) => tag.toLowerCase().includes(q))
    );
  }, [positions, searchValue]);

  // Paginacja po stronie klienta na przefiltrowanej liście.
  const pageCount = Math.max(1, Math.ceil(filteredPositions.length / PAGE_LIMIT));
  const safePage = Math.min(page, pageCount);
  const paginatedPositions = useMemo(
    () => filteredPositions.slice((safePage - 1) * PAGE_LIMIT, safePage * PAGE_LIMIT),
    [filteredPositions, safePage]
  );

  // Reset strony do 1, gdy zmienia się filtr (żeby nie utknąć poza zakresem).
  React.useEffect(() => {
    setPage(1);
  }, [searchValue]);

  const filteredOnPageIds = useMemo(
    () => paginatedPositions.map((p) => p.id),
    [paginatedPositions]
  );

  const selectedOnPageCount = useMemo(
    () => filteredOnPageIds.filter((id) => selectedIds.has(id)).length,
    [filteredOnPageIds, selectedIds]
  );

  const allOnPageSelected =
    filteredOnPageIds.length > 0 && selectedOnPageCount === filteredOnPageIds.length;
  const someOnPageSelected =
    selectedOnPageCount > 0 && selectedOnPageCount < filteredOnPageIds.length;

  const handleToggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleToggleThresholds = (id: number) => {
    setThresholdsUserToggledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sekcja progów jest domyślnie rozwinięta, jeśli pozycja ma już zapisane progi.
  // User może ręcznie przełączyć w obie strony — wtedy `thresholdsUserToggledIds`
  // trzyma "odstępstwo" od defaultu.
  const isThresholdsSectionOpen = (product: IRenderPosition): boolean => {
    const defaultOpen = (product.priceThresholds?.length ?? 0) > 0;
    const userToggled = thresholdsUserToggledIds.has(product.id);
    return userToggled ? !defaultOpen : defaultOpen;
  };

  const handleDelete = (positionId: number) => {
    if (isDeleting) return;
    if (!window.confirm(t('Czy na pewno usunąć pozycję z oferty?'))) return;
    deletePosition(positionId);
  };

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, positionId: number) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setRowMenuPositionId(positionId);
  };

  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuPositionId(null);
  };

  const handleDeleteFromMenu = () => {
    const id = rowMenuPositionId;
    closeRowMenu();
    if (id !== null) {
      handleDelete(id);
    }
  };

  const toggleSelected = (positionId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) {
        next.delete(positionId);
      } else {
        next.add(positionId);
      }
      return next;
    });
  };

  const toggleSelectAllOnPage = (filteredOnPageIds: number[]) => {
    setSelectedIds((prev) => {
      const allSelected = filteredOnPageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        filteredOnPageIds.forEach((id) => next.delete(id));
      } else {
        filteredOnPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const openBulkMenu = (event: React.MouseEvent<HTMLElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const closeBulkMenu = () => {
    setBulkMenuAnchor(null);
  };

  const handleBulkDelete = async () => {
    closeBulkMenu();
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || isBulkDeleting) return;
    if (
      !window.confirm(
        t('Czy na pewno usunąć {{count}} pozycji z oferty?', { count: ids.length })
      )
    ) {
      return;
    }
    setIsBulkDeleting(true);
    try {
      await Promise.all(ids.map((id) => deletePositionAsync(id)));
      setSelectedIds(new Set());
    } finally {
      setIsBulkDeleting(false);
      invalidate();
    }
  };

  /**
   * Otwiera modal masowego ustawiania rabatu. Sam request wychodzi z `handleBulkSetDiscountSubmit`.
   */
  const handleOpenBulkDiscountModal = () => {
    closeBulkMenu();
    if (selectedIds.size === 0 || isBulkUpdating) return;
    setIsBulkDiscountModalOpen(true);
  };

  /**
   * Submit z modala: PUT na wszystkich zaznaczonych pozycjach (równolegle).
   * Backend: discountValue ma pierwszeństwo nad discountPct - zerujemy "drugi" rabat,
   * żeby user dostał dokładnie ten typ, który wybrał.
   */
  const handleBulkSetDiscountSubmit = async (mode: 'pct' | 'value', value: number) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || isBulkUpdating) return;

    // Wysylamy 0 (a nie null) dla "drugiego" rabatu - BE pomija pola === null
    // ("nie zmieniaj"), tylko jawne 0 nadpisze stara wartosc w bazie.
    const payload =
      mode === 'pct'
        ? { discountPct: value, discountValue: 0 }
        : { discountPct: 0, discountValue: value };

    setIsBulkUpdating(true);
    try {
      await Promise.all(
        ids.map((positionId) => updatePositionAsync({ positionId, payload }))
      );
      setSelectedIds(new Set());
      setIsBulkDiscountModalOpen(false);
    } finally {
      setIsBulkUpdating(false);
      invalidate();
    }
  };

  /**
   * Maksymalna ilość możliwa do zamówienia dla pozycji:
   * - gdy wybrano magazyn → stan tego magazynu,
   * - gdy brak wyboru → suma stanów ze wszystkich magazynów,
   * - gdy brak danych o stockach → null (bez ograniczenia).
   */
  const getMaxQtyForPosition = (position: IRenderPosition): number | null => {
    if (position.stocks.length === 0) return null;
    if (position.warehouseId !== null) {
      const stock = position.stocks.find((s) => s.warehouseId === position.warehouseId);
      return stock ? stock.quantity : 0;
    }
    return position.stocks.reduce((sum, s) => sum + s.quantity, 0);
  };

  const handleQuantityChange = (positionId: number, qty: number) => {
    if (Number.isNaN(qty)) return;
    const position = positions.find((p) => p.id === positionId);
    // Oferta handlowca nie jest ograniczona stanem magazynowym — klient może
    // zamawiać ilość większą niż aktualny stock (oferta != zamówienie).
    // Stan magazynowy jest pokazywany jako informacja (limitReached) ale nie blokuje wartości.
    const clamped = qty < 1 ? 1 : qty;
    if (position && clamped === position.qty) return;
    updatePosition({ positionId, payload: { quantity: clamped } });
  };

  const handleWarehouseChange = (positionId: number, value: string) => {
    const parsed = value === '' ? null : Number(value);
    if (parsed !== null && (Number.isNaN(parsed) || parsed <= 0)) return;

    // Po zmianie magazynu trzeba ponownie obciąć ilość do nowego limitu.
    const position = positions.find((p) => p.id === positionId);
    const payload: { warehouseId?: number; quantity?: number } = {
      warehouseId: parsed ?? undefined
    };

    if (position) {
      const stockForNewWarehouse =
        parsed !== null
          ? position.stocks.find((s) => s.warehouseId === parsed)?.quantity ?? 0
          : position.stocks.reduce((sum, s) => sum + s.quantity, 0);
      if (position.qty > stockForNewWarehouse && stockForNewWarehouse > 0) {
        payload.quantity = stockForNewWarehouse;
      }
    }

    updatePosition({ positionId, payload });
  };

  const handleDiscountPctChange = (positionId: number, value: string) => {
    const trimmed = value.trim();
    // Pusty input = wyzeruj rabat (0). null nie zadzialaby - PUT pomija pola === null.
    const parsed = trimmed === '' ? 0 : parseInt(trimmed, 10);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return;
    updatePosition({ positionId, payload: { discountPct: parsed } });
  };

  const handleDiscountValueChange = (positionId: number, value: string) => {
    const trimmed = value.trim();
    // Pusty input = wyzeruj rabat (0). null nie zadzialaby - PUT pomija pola === null.
    if (trimmed === '') {
      updatePosition({ positionId, payload: { discountValue: 0 } });
      return;
    }
    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) return;
    updatePosition({ positionId, payload: { discountValue: parsed } });
  };

  const renderTags = (tags: string[]) =>
    tags.map((tag, i) => (
      <React.Fragment key={`${tag}-${i}`}>
        {i > 0 && <span className={styles.tagSep}>|</span>}
        <span>{tag}</span>
      </React.Fragment>
    ));

  const renderExpandedContent = (product: IRenderPosition) => (
    <tr className={styles.expandedContent}>
      <td colSpan={10}>
        <div className={styles.expandedInner}>
          {/* 3 karty dolne */}
          <div className={styles.expandedCards}>
            {/* Komentarze i zalaczniki - realne dane z API */}
            <PositionCommentsCard
              clientId={clientId}
              offerId={offerId}
              positionId={product.id}
              versionId={versionId}
              onAddComment={() => setCommentForPositionId(product.id)}
            />

            <div className={styles.expandedCard}>
              <div className={styles.expandedCardTitle}>{t('Upsale')}</div>
              {product.upsellProducts.length === 0 ? (
                <div className={styles.upsaleList}>
                  <div className={styles.upsaleItem}>
                    <span className={styles.upsaleName}>{t('Brak produktów upsell.')}</span>
                  </div>
                </div>
              ) : (
                <div className={styles.upsaleList}>
                  {product.upsellProducts.map((up) => {
                    const searchKeyword =
                      up.symbol && up.symbol.trim().length > 0
                        ? up.symbol
                        : up.productName ?? '';
                    const label = up.productName ?? up.symbol ?? `#${up.id}`;
                    return (
                      <div key={up.id} className={styles.upsaleItem}>
                        <span className={styles.upsaleName}>{label}</span>
                        <button
                          type="button"
                          className={styles.upsaleAddBtn}
                          onClick={() =>
                            setUpsellSelection({ productId: up.id, searchKeyword })
                          }>
                          {t('DODAJ POZYCJĘ')} <PlusCircleIcon className={styles.plusCircleSm} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Lista usług per-pozycja - separator nad listą tylko gdy są też produkty upsell.
                  Komponent zwraca null gdy brak usług (świadomie nie pokazujemy pustej sekcji). */}
              <PositionUpsaleServicesList
                clientId={clientId}
                offerId={offerId}
                positionId={product.id}
                isOfferDraft={isOfferDraft}
                showDivider={product.upsellProducts.length > 0}
              />

              {/* Podglad pozycji kalkulacji (read-only). Edycja w modalu - link "Pokaz kalkulacje"
                  pod nazwa produktu. Komponent renderuje sie tylko gdy pozycja ma kalkulacje
                  z co najmniej jedna pozycja. */}
              {product.calculation && (
                <PositionCalculationSummary
                  clientId={clientId}
                  offerId={offerId}
                  calculationId={product.calculation.id}
                  calculationName={product.calculation.name}
                  unit={product.unit}
                  showDivider
                />
              )}
            </div>

            {/* Dane kosztowe */}
            <div className={styles.expandedCard}>
              <div className={styles.expandedCardTitle}>{t('Dane kosztowe')}</div>
              <div className={styles.costList}>
                <div className={styles.costItem}>
                  <div className={styles.costLabel}>{t('Wartość netto pozycji:')}</div>
                  <div className={styles.costValue}>
                    {formatPrice(product.priceNetSalesRaw * product.qty)}
                  </div>
                </div>
                <div className={classnames(styles.costItem, styles.costSpacer)}>
                  <div className={styles.costLabel}>{t('Wartość brutto pozycji:')}</div>
                  <div className={styles.costValue}>
                    {formatPrice(product.priceGrossSalesRaw * product.qty)}
                  </div>
                </div>
                <hr className={styles.costDivider} />
                <div className={styles.costItem}>
                  <div className={styles.costLabel}>{t('Koszt zakupu:')}</div>
                  <div className={styles.costValue}>
                    {product.purchaseCostNet !== null
                      ? formatPrice(product.purchaseCostNet)
                      : t('Brak danych w bazie')}
                  </div>
                </div>
                <div className={styles.costItem}>
                  <div className={styles.costLabel}>{t('Minimalna cena sprzedaży:')}</div>
                  <div className={styles.costValue}>
                    {product.minSalesNet !== null
                      ? formatPrice(product.minSalesNet)
                      : t('Brak danych w bazie')}
                  </div>
                </div>
                <div className={classnames(styles.costItem, styles.costSpacer)}>
                  <div className={styles.costLabel}>{t('Marża / szt:')}</div>
                  <div className={styles.costValue}>
                    {product.marginNet !== null
                      ? formatPrice(product.marginNet)
                      : t('Brak danych w bazie')}
                  </div>
                </div>
                <div className={styles.costItem}>
                  <div className={styles.costLabel}>{t('Marża pozycji (razem):')}</div>
                  <div className={styles.costValue}>
                    {product.marginNetTotal !== null
                      ? formatPrice(product.marginNetTotal)
                      : t('Brak danych w bazie')}
                  </div>
                </div>
                {/* Koszty własne - wyłączone do czasu ustalenia logiki wyliczania.
                <div className={styles.costItem}>
                  <div className={styles.costLabel}>{t('Koszty własne:')}</div>
                  <div className={styles.costValue}>
                    {product.ownCostsNet !== null
                      ? formatPrice(product.ownCostsNet)
                      : t('Brak danych w bazie')}
                  </div>
                </div>
                */}
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        'StylePath-Containers-TraderClientOfferProductsContainer'
      )}>
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <button
            className={styles.actionsBtn}
            onClick={openBulkMenu}
            disabled={selectedIds.size === 0 || isBulkDeleting || isBulkUpdating}
            aria-haspopup="menu"
            aria-expanded={bulkMenuAnchor !== null}>
            <MoreVertIcon style={{ fontSize: 16 }} />
            {t('AKCJE')}
            {selectedIds.size > 0 && ` (${selectedIds.size})`}
          </button>
          <button
            className={styles.addPositionBtn}
            onClick={() => setIsAddPositionOpen(true)}>
            <AddIcon style={{ fontSize: 14 }} />
            {t('DODAJ POZYCJĘ')}
          </button>
        </div>
        <div className={styles.toolbarRight}>
          <div className={styles.searchBox}>
            <SearchIcon style={{ fontSize: 18, color: '#8d9096' }} />
            <input
              type="text"
              placeholder={t('Filtruj po nazwie, kodzie, SKU, etc.')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <colgroup>
            <col className={styles.colCheckbox} />
            <col className={styles.lpCol} />
            <col className={styles.colProducts} />
            <col className={styles.colMagazyn} />
            <col className={styles.colIlosc} />
            <col className={styles.colPrice} />
            <col className={styles.colPrice} />
            <col className={styles.colPriceBold} />
            <col className={styles.colPriceGross} />
            <col className={styles.colActionsGroup} />
          </colgroup>
          <thead className={styles.thead}>
            <tr>
              <th className={styles.thCheck} rowSpan={2}>
                <Checkbox
                  size="small"
                  sx={{ padding: '2px' }}
                  checked={allOnPageSelected}
                  indeterminate={someOnPageSelected}
                  onChange={() => toggleSelectAllOnPage(filteredOnPageIds)}
                  inputProps={{ 'aria-label': t('Zaznacz wszystkie') }}
                />
              </th>
              <th className={classnames(styles.th, styles.lpCol)} rowSpan={2}>
                {t('Lp')} <span className={styles.sortIcon}>↓</span>
              </th>
              <th className={classnames(styles.th, styles.colProducts)} rowSpan={2}>
                {t('Produkty')} <span className={styles.sortIcon}>↕</span>
              </th>
              <th className={classnames(styles.th, styles.colMagazyn)} rowSpan={2}>
                {t('Magazyn')} <span className={styles.sortIcon}>↑</span>
              </th>
              <th className={classnames(styles.th, styles.colIlosc)} rowSpan={2}>
                {t('Ilość')} <span className={styles.sortIcon}>↑</span>
              </th>
              <th className={classnames(styles.thGroup)} colSpan={3}>
                {t('Cena netto')}
              </th>
              <th className={classnames(styles.th, styles.colPriceGross)} rowSpan={2}>
                {t('Cena brutto')} <span className={styles.sortIcon}>↑</span>
              </th>
              <th className={styles.thActions} rowSpan={2}>
                {t('Akcje')}
              </th>
            </tr>
            <tr>
              <th className={classnames(styles.th, styles.colPrice, styles.colPriceFirst)}>{t('Katalogowa')}</th>
              <th className={classnames(styles.th, styles.colPrice)}>{t('Rabatowa')}</th>
              <th className={classnames(styles.th, styles.colPriceBold)}>{t('Sprzedażowa')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '40px 0' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            )}
            {!isLoading && isError && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '24px 0', color: '#e02e4e' }}>
                  {t('Nie udało się pobrać danych oferty.')}
                </td>
              </tr>
            )}
            {!isLoading && !isError && paginatedPositions.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '24px 0', color: '#8d9096' }}>
                  {searchValue.trim()
                    ? t('Brak pozycji pasujących do filtra.')
                    : t('Brak pozycji w ofercie.')}
                </td>
              </tr>
            )}
            {!isLoading &&
              !isError &&
              paginatedPositions.map((product) => {
                const isExpanded = expandedId === product.id;
                return (
                  <React.Fragment key={product.id}>
                    <tr
                      className={classnames(styles.tr, { [styles.expandedRow]: isExpanded })}>
                      {/* Rozwijanie/zwijanie pozycji - WYLACZNIE przyciskiem strzalki po prawej (.expandBtn).
                          Wczesniej caly <tr> miał onClick, ale powodowalo to przypadkowe zwijanie
                          przy klikaniu w komorki (np. textfield ilosci). */}

                      {/* Checkbox - bez zmian w obu trybach */}
                      <td className={styles.tdCheck}>
                        <div className={styles.checkCellInner}>
                          <Checkbox
                            size="small"
                            sx={{ padding: '2px' }}
                            checked={selectedIds.has(product.id)}
                            onChange={() => toggleSelected(product.id)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': t('Zaznacz pozycję') }}
                          />
                          {product.hasComment && (
                            <button className={styles.rowIconBtn}>
                              <ChatBubbleOutlineIcon style={{ fontSize: 13 }} />
                            </button>
                          )}
                          {product.hasAttachment && (
                            <button className={styles.rowIconBtn}>
                              <AttachmentIcon className={styles.rowIconSvg} />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Lp - bez zmian */}
                      <td className={classnames(styles.td, styles.lpCol)}>{product.lp}</td>

                      {/* Produkty */}
                      <td className={styles.td}>
                        {isExpanded ? (
                          <div className={styles.editProductCell}>
                            <ProductImage src={product.imageUrl} className={styles.expandedProductImgPlaceholder} />
                            <div className={styles.expandedProductInfo}>
                              <div className={styles.editProductNameRow}>
                                <span className={styles.expandedProductName}>{product.name}</span>
                                <ExpandMoreIcon style={{ fontSize: 14, color: '#8d9096' }} />
                              </div>
                              {product.category && (
                                <div className={styles.expandedProductCat}>{product.category}</div>
                              )}
                              <div className={styles.productTags}>
                                {product.tags.length > 0
                                  ? renderTags(product.tags)
                                  : (
                                    <React.Fragment>
                                      <span>ELKO</span>
                                      <span className={styles.tagSep}>|</span>
                                      <span>Muller</span>
                                      <span className={styles.tagSep}>|</span>
                                      <span>0621-...</span>
                                      <span className={styles.tagSep}>|</span>
                                      <span>ELKO_SGFY...</span>
                                    </React.Fragment>
                                  )}
                              </div>
                              {/* Link "DODAJ KALKULACJĘ" / "POKAŻ KALKULACJĘ" - dostępny tylko gdy oferta jest w stanie DRAFT.
                                  Jeśli pozycja ma już przypisaną kalkulację — otwiera od razu modal #2
                                  (CalculationPositionsModal) z jej pozycjami. W przeciwnym razie modal #1
                                  (ChooseCalculationActionModal) z wyborem akcji. */}
                              {isOfferDraft && (() => {
                                const existingCalc = product.calculation;
                                return (
                                  <button
                                    type="button"
                                    className={styles.addServiceLink}
                                    onClick={() =>
                                      existingCalc
                                        ? setCalcModal({
                                            kind: 'positions',
                                            positionId: product.id,
                                            productName: product.name,
                                            calculationId: existingCalc.id,
                                            calculationName: existingCalc.name,
                                            calculationDescription: ''
                                          })
                                        : setCalcModal({
                                            kind: 'chooseAction',
                                            positionId: product.id,
                                            productName: product.name
                                          })
                                    }>
                                    {t(
                                      existingCalc
                                        ? 'salesCrm.calculation.viewCalculationCta'
                                        : 'salesCrm.calculation.addCalculationCta'
                                    )}{' '}
                                    <PlusCircleIcon className={styles.plusCircleSm} />
                                  </button>
                                );
                              })()}
                            </div>
                          </div>
                        ) : (
                          <div className={styles.productCell}>
                            <ProductImage src={product.imageUrl} className={styles.productImgPlaceholder} />
                            <div className={styles.productInfo}>
                              <div className={styles.productName}>{product.name}</div>
                              <div className={styles.productCategory}>{product.category}</div>
                              <div className={styles.productTags}>{renderTags(product.tags)}</div>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Magazyn */}
                      <td className={styles.td} data-mobile-label={t('Magazyn')}>
                        {(() => {
                          const selectedStock =
                            product.warehouseId !== null
                              ? product.stocks.find((s) => s.warehouseId === product.warehouseId) ?? null
                              : null;
                          const stockName = selectedStock?.name ?? t('Brak magazynu');
                          const stockQtyText =
                            selectedStock !== null
                              ? `${selectedStock.quantity} ${product.unit}`
                              : '-';

                          const stocksTooltip =
                            product.stocks.length > 0 ? (
                              <div>
                                {product.stocks.map((stock) => (
                                  <div key={stock.warehouseId}>
                                    {stock.name}: {stock.quantity} {product.unit}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              t('Brak danych o stanach magazynowych')
                            );

                          if (isExpanded) {
                            return (
                              <div className={styles.editStockCell}>
                                <select
                                  className={styles.expandedSelect}
                                  value={product.warehouseId ?? ''}
                                  onChange={(e) => handleWarehouseChange(product.id, e.target.value)}>
                                  {product.warehouseId === null && (
                                    <option value="">{t('Wybierz magazyn')}</option>
                                  )}
                                  {product.stocks.map((stock) => (
                                    <option key={stock.warehouseId} value={stock.warehouseId}>
                                      {stock.name} ({stock.quantity} {product.unit})
                                    </option>
                                  ))}
                                </select>
                                <div className={styles.expandedStockRow}>
                                  <span className={styles.expandedStockQtyText}>{stockQtyText}</span>
                                  <Tooltip title={stocksTooltip} arrow placement="top">
                                    <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
                                      <WarehouseIcon className={styles.warehouseIcon} />
                                    </span>
                                  </Tooltip>
                                </div>
                              </div>
                            );
                          }

                          return (
                            <div className={styles.stockCell}>
                              <div className={styles.stockName}>{stockName}</div>
                              <div className={styles.stockQty}>
                                <span className={styles.stockQtyText}>{stockQtyText}</span>
                                <Tooltip title={stocksTooltip} arrow placement="top">
                                  <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
                                    <WarehouseIcon className={styles.warehouseIcon} />
                                  </span>
                                </Tooltip>
                              </div>
                            </div>
                          );
                        })()}
                      </td>

                      {/* Ilość */}
                      <td className={styles.td} data-mobile-label={t('Ilość')}>
                        {(() => {
                          const maxQty = getMaxQtyForPosition(product);
                          const variantsMode = isVariantsModeActive(
                            product.priceThresholds,
                            product.priceThresholdsAsVariants
                          );
                          const variantOptions = variantsMode
                            ? [...(product.priceThresholds ?? [])].sort(
                                (a, b) => a.qtyFrom - b.qtyFrom
                              )
                            : [];
                          return isExpanded ? (
                          <div className={styles.editQtyCell}>
                            <div className={styles.editQtyInputRow}>
                              {variantsMode ? (
                                <select
                                  key={`qty-variant-${product.id}-${product.qty}`}
                                  className={styles.expandedSelect}
                                  value={product.qty}
                                  onChange={(e) =>
                                    handleQuantityChange(product.id, parseFloat(e.target.value))
                                  }>
                                  {variantOptions.every(
                                    (opt) => opt.qtyFrom !== product.qty
                                  ) && (
                                    <option value={product.qty} disabled>
                                      {product.qty} {product.unit} ({t('poza wariantami')})
                                    </option>
                                  )}
                                  {variantOptions.map((opt) => (
                                    <option key={opt.qtyFrom} value={opt.qtyFrom}>
                                      {opt.qtyFrom} {product.unit} — {formatPrice(opt.price)}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <>
                                  <input
                                    key={`qty-${product.id}-${product.qty}`}
                                    type="number"
                                    className={styles.expandedInput}
                                    defaultValue={product.qty}
                                    min={1}
                                    max={maxQty ?? undefined}
                                    step={1}
                                    onBlur={(e) =>
                                      handleQuantityChange(
                                        product.id,
                                        parseFloat(e.target.value)
                                      )
                                    }
                                  />
                                  {versionId === null ? (
                                    <button
                                      type="button"
                                      className={styles.unitButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingUnitPositionId(product.id);
                                      }}
                                      title={t('Zmien jednostke')}>
                                      {product.unit}
                                    </button>
                                  ) : (
                                    <span>{product.unit}</span>
                                  )}
                                </>
                              )}
                            </div>
                            {!variantsMode && (
                              <div className={styles.qtyMin}>
                                {'Min:'} {product.qtyMin} {product.unit}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className={styles.qtyCell}>
                            <div className={styles.qtyValue}>
                              {variantsMode ? (
                                <select
                                  key={`qty-variant-collapsed-${product.id}-${product.qty}`}
                                  className={styles.collapsedQtySelect}
                                  value={product.qty}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      product.id,
                                      parseFloat(e.target.value)
                                    )
                                  }>
                                  {variantOptions.every(
                                    (opt) => opt.qtyFrom !== product.qty
                                  ) && (
                                    <option value={product.qty} disabled>
                                      {product.qty} {product.unit}
                                    </option>
                                  )}
                                  {variantOptions.map((opt) => (
                                    <option key={opt.qtyFrom} value={opt.qtyFrom}>
                                      {opt.qtyFrom} {product.unit}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <>
                                  <input
                                    key={`qty-collapsed-${product.id}-${product.qty}`}
                                    type="number"
                                    className={styles.collapsedQtyInput}
                                    defaultValue={product.qty}
                                    min={1}
                                    step={1}
                                    onClick={(e) => e.stopPropagation()}
                                    onBlur={(e) =>
                                      handleQuantityChange(
                                        product.id,
                                        parseFloat(e.target.value)
                                      )
                                    }
                                  />
                                  {versionId === null ? (
                                    <button
                                      type="button"
                                      className={styles.unitButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingUnitPositionId(product.id);
                                      }}
                                      title={t('Zmien jednostke')}>
                                      {product.unit}
                                    </button>
                                  ) : (
                                    <span>{product.unit}</span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className={styles.qtyMin}>
                              {'Min:'} {product.qtyMin} {product.unit}
                            </div>
                          </div>
                        );
                        })()}
                      </td>

                      {/* Katalogowa */}
                      <td
                        className={classnames(styles.td, styles.colPriceFirst)}
                        data-mobile-label={t('Cena katalogowa')}>
                        <div className={styles.priceCell}>
                          <div className={styles.priceValue}>{product.priceCatalog}</div>
                        </div>
                      </td>

                      {/* Rabatowa */}
                      <td className={styles.td} data-mobile-label={t('Cena rabatowa')}>
                        <div className={styles.priceCell}>
                          <div className={styles.priceValue}>{product.priceDiscount}</div>
                        </div>
                      </td>

                      {/* Sprzedażowa */}
                      <td className={styles.td} data-mobile-label={t('Cena sprzedażowa')}>
                        {isExpanded ? (
                          <div className={styles.priceCell}>
                            <div className={styles.priceValueBold}>{product.priceSale}</div>
                            {(() => {
                              const discountsDisabled = hasRealThresholds(product.priceThresholds);
                              const disabledTooltip = t(
                                'Rabaty są wyłączone, ponieważ cena sprzedażowa jest sterowana ustawionymi progami cenowymi.'
                              );
                              const discountMode = getDiscountMode(
                                product.id,
                                product.discountPct,
                                product.discountValue
                              );
                              const isValueMode = discountMode === 'value';
                              const discountsBlock = (
                                <div
                                  className={classnames(styles.editDiscountBlock, {
                                    [styles.editDiscountBlockDisabled]: discountsDisabled
                                  })}>
                                  <div className={styles.editDiscountRow}>
                                    <span className={styles.editDiscountLabel}>
                                      {t('Rabat')}
                                      <Tooltip
                                        title={t(
                                          'Mozna ustawic tylko jeden rodzaj rabatu - przelacz typ aby zmienic z procentowego na wartosciowy.'
                                        )}
                                        arrow
                                        placement="top">
                                        <InfoOutlinedIcon
                                          style={{
                                            fontSize: 13,
                                            color: '#8d9096',
                                            cursor: 'help'
                                          }}
                                        />
                                      </Tooltip>
                                      <span className={styles.discountModeToggle}>
                                        <button
                                          type="button"
                                          className={classnames(styles.discountModeBtn, {
                                            [styles.discountModeBtnActive]: isValueMode
                                          })}
                                          disabled={discountsDisabled}
                                          onClick={() => {
                                            if (!isValueMode) switchDiscountMode(product.id, 'value');
                                          }}>
                                          zł
                                        </button>
                                        <button
                                          type="button"
                                          className={classnames(styles.discountModeBtn, {
                                            [styles.discountModeBtnActive]: !isValueMode
                                          })}
                                          disabled={discountsDisabled}
                                          onClick={() => {
                                            if (isValueMode) switchDiscountMode(product.id, 'pct');
                                          }}>
                                          %
                                        </button>
                                      </span>
                                    </span>
                                    {isValueMode ? (
                                      <div className={styles.editDiscountControl}>
                                        <WalletIcon className={styles.editDiscountIcon} />
                                        <input
                                          key={`discount-value-${product.id}-${product.discountValue}`}
                                          type="number"
                                          step="0.01"
                                          min={0}
                                          className={styles.editDiscountInput}
                                          defaultValue={product.discountValue}
                                          disabled={discountsDisabled}
                                          onBlur={(e) =>
                                            handleDiscountValueChange(product.id, e.target.value)
                                          }
                                        />
                                        <span className={styles.editDiscountPct}>zł</span>
                                      </div>
                                    ) : (
                                      <div className={styles.editDiscountControl}>
                                        <DiscountIcon className={styles.editDiscountIcon} />
                                        <input
                                          key={`discount-pct-${product.id}-${product.discountPct}`}
                                          type="number"
                                          className={styles.editDiscountInput}
                                          defaultValue={product.discountPct}
                                          min={0}
                                          max={100}
                                          disabled={discountsDisabled}
                                          onBlur={(e) =>
                                            handleDiscountPctChange(product.id, e.target.value)
                                          }
                                        />
                                        <span className={styles.editDiscountPct}>%</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );

                              return discountsDisabled ? (
                                <Tooltip title={disabledTooltip} arrow placement="top">
                                  {discountsBlock}
                                </Tooltip>
                              ) : (
                                discountsBlock
                              );
                            })()}
                          </div>
                        ) : (
                          <div className={styles.priceCell}>
                            <div className={styles.priceValueBold}>{product.priceSale}</div>
                            <div className={styles.priceTags}>
                              {product.discountValue > 0 ? (
                                <>
                                  <span
                                    className={classnames(
                                      styles.discountTag,
                                      styles.discountTagPrimary
                                    )}>
                                    <WalletIcon className={styles.discountTagIcon} />
                                    {product.discountValue.toFixed(2).replace('.', ',')} zł
                                  </span>
                                  {product.discountPct > 0 && (
                                    <span
                                      className={classnames(
                                        styles.discountTag,
                                        styles.discountTagSecondary
                                      )}>
                                      <DiscountIcon className={styles.discountTagIcon} />
                                      {product.discountPct}%
                                    </span>
                                  )}
                                </>
                              ) : product.discountPct > 0 ? (
                                <span
                                  className={classnames(
                                    styles.discountTag,
                                    styles.discountTagPrimary
                                  )}>
                                  <DiscountIcon className={styles.discountTagIcon} />
                                  {product.discountPct}%
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Cena brutto */}
                      <td
                        className={classnames(styles.td, styles.tdPriceGross)}
                        data-mobile-label={t('Cena brutto')}>
                        <div className={styles.priceCell}>
                          <div className={styles.priceValueBold}>{product.priceGross}</div>
                        </div>
                      </td>

                      {/* Akcje */}
                      <td className={styles.tdActions}>
                        <div className={styles.actionIcons}>
                          <button
                            className={styles.kebabBtn}
                            title={t('Akcje')}
                            aria-label={t('Akcje')}
                            aria-haspopup="menu"
                            aria-expanded={rowMenuPositionId === product.id}
                            onClick={(e) => openRowMenu(e, product.id)}>
                            <MoreVertIcon style={{ fontSize: 16 }} />
                          </button>
                          <button
                            className={classnames(styles.expandBtn, {
                              [styles.expanded]: isExpanded
                            })}
                            onClick={() => handleToggleExpand(product.id)}>
                            <ExpandMoreIcon style={{ fontSize: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className={styles.thresholdsRow}>
                        <td className={styles.thresholdsToggleCell} colSpan={5} />
                        <td className={styles.thresholdsToggleCell} colSpan={3}>
                          <button
                            type="button"
                            className={classnames(styles.thresholdsToggleBtn, {
                              [styles.thresholdsToggleBtnOpen]: isThresholdsSectionOpen(product)
                            })}
                            onClick={() => handleToggleThresholds(product.id)}
                            aria-expanded={isThresholdsSectionOpen(product)}>
                            <ExpandMoreIcon
                              className={styles.thresholdsToggleIcon}
                              style={{ fontSize: 16 }}
                            />
                            <span className={styles.thresholdsToggleLabel}>
                              {t('Progi cenowe')}
                            </span>
                            {product.priceThresholds && product.priceThresholds.length > 0 && (
                              <span className={styles.thresholdsCountBadge}>
                                {product.priceThresholds.length}
                              </span>
                            )}
                          </button>
                          {isThresholdsSectionOpen(product) && (
                            <div className={styles.thresholdsEditorWrap}>
                              <PriceThresholdsEditor
                                clientId={clientId}
                                offerId={offerId}
                                positionId={product.id}
                                positionPriceNetSales={product.priceNetSalesRaw}
                                positionQty={product.qty}
                                positionUnit={product.unit}
                                thresholds={product.priceThresholds}
                                asVariants={product.priceThresholdsAsVariants}
                                versionId={versionId}
                                readOnly={versionId !== null}
                              />
                            </div>
                          )}
                        </td>
                        <td className={styles.thresholdsToggleCell} colSpan={2} />
                      </tr>
                    )}
                    {isExpanded && renderExpandedContent(product)}
                  </React.Fragment>
                );
              })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className={styles.pagination}>
          <Pagination
            count={pageCount}
            page={safePage}
            onChange={(_e, val) => setPage(val)}
            shape="rounded"
            size="small"
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: '#6b54ff',
                color: '#fff',
                '&:hover': { backgroundColor: '#5a47e0' }
              }
            }}
          />
        </div>
      )}

      <Menu
        anchorEl={rowMenuAnchor}
        open={rowMenuAnchor !== null}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={handleDeleteFromMenu} disabled={isDeleting}>
          <DeleteOutlineIcon style={{ fontSize: 16, marginRight: 8, color: '#e02e4e' }} />
          {t('Usuń pozycję')}
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={bulkMenuAnchor}
        open={bulkMenuAnchor !== null}
        onClose={closeBulkMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
        <MenuItem
          onClick={handleOpenBulkDiscountModal}
          disabled={selectedIds.size === 0 || isBulkUpdating}>
          {t('Ustaw rabat')} ({selectedIds.size})
        </MenuItem>
        <MenuItem
          onClick={handleBulkDelete}
          disabled={selectedIds.size === 0 || isBulkDeleting}>
          <DeleteOutlineIcon style={{ fontSize: 16, marginRight: 8, color: '#e02e4e' }} />
          {t('Usuń zaznaczone')} ({selectedIds.size})
        </MenuItem>
      </Menu>

      <AddOfferPositionModal
        open={isAddPositionOpen}
        clientId={clientId}
        offerId={offerId}
        onClose={() => setIsAddPositionOpen(false)}
        onAdded={invalidate}
      />

      {upsellSelection !== null && (
        <AddOfferPositionModal
          open
          clientId={clientId}
          offerId={offerId}
          initialSearchKeyword={upsellSelection.searchKeyword}
          initialProductId={upsellSelection.productId}
          onClose={() => setUpsellSelection(null)}
          onAdded={invalidate}
        />
      )}

      <BulkSetDiscountModal
        open={isBulkDiscountModalOpen}
        count={selectedIds.size}
        isSubmitting={isBulkUpdating}
        onClose={() => setIsBulkDiscountModalOpen(false)}
        onSubmit={handleBulkSetDiscountSubmit}
      />

      {commentForPositionId !== null && (
        <AddPositionCommentModal
          open={commentForPositionId !== null}
          clientId={clientId}
          offerId={offerId}
          positionId={commentForPositionId}
          onClose={() => setCommentForPositionId(null)}
        />
      )}

      {/* ====== Calculation flow modals (state machine) ====== */}
      <ChooseCalculationActionModal
        open={calcModal.kind === 'chooseAction'}
        onClose={() => setCalcModal({ kind: 'closed' })}
        onCreateNew={handleOpenCreateForm}
        onCreateFromExisting={() => {
          if (calcModal.kind !== 'chooseAction') return;
          setCalcModal({
            kind: 'chooseTemplate',
            positionId: calcModal.positionId,
            productName: calcModal.productName
          });
        }}
        onLink={() => {
          if (calcModal.kind !== 'chooseAction') return;
          setCalcModal({
            kind: 'link',
            positionId: calcModal.positionId,
            productName: calcModal.productName
          });
        }}
        productName={calcModal.kind === 'chooseAction' ? calcModal.productName : ''}
      />

      {calcModal.kind === 'link' && (
        <LinkCalculationModal
          open
          onClose={() => setCalcModal({ kind: 'closed' })}
          clientId={clientId}
          offerId={offerId}
          positionId={calcModal.positionId}
          productName={calcModal.productName}
          onLinkedSuccess={({ id, name }) => {
            if (calcModal.kind !== 'link') return;
            invalidate();
            setCalcModal({
              kind: 'positions',
              positionId: calcModal.positionId,
              productName: calcModal.productName,
              calculationId: id,
              calculationName: name,
              calculationDescription: ''
            });
          }}
        />
      )}

      <CreateCalculationModal
        open={calcModal.kind === 'createForm'}
        onClose={() => setCalcModal({ kind: 'closed' })}
        onSubmit={handleCreateCalculationSubmit}
        initialName={calcModal.kind === 'createForm' ? calcModal.productName : ''}
        isSubmitting={isCreatingCalculation}
      />

      {calcModal.kind === 'chooseTemplate' && (
        <ChooseCalculationTemplateModal
          open
          onClose={() => setCalcModal({ kind: 'closed' })}
          clientId={clientId}
          offerId={offerId}
          positionId={calcModal.positionId}
          productName={calcModal.productName}
          onClonedSuccess={({ id, name }) => {
            // Po sklonowaniu: invaliduj pozycje oferty (link "Pokaz kalkulacje" pojawi sie),
            // dane oferty (ceny pozycji oferty zaleza od kalkulacji).
            invalidate();
            if (calcModal.kind !== 'chooseTemplate') return;
            setCalcModal({
              kind: 'positions',
              positionId: calcModal.positionId,
              productName: calcModal.productName,
              calculationId: id,
              calculationName: name,
              calculationDescription: ''
            });
          }}
        />
      )}

      {(calcModal.kind === 'positions'
        || calcModal.kind === 'addPosition'
        || calcModal.kind === 'editPosition') && (
        <CalculationPositionsModal
          open
          onClose={() => setCalcModal({ kind: 'closed' })}
          onAddPosition={() =>
            setCalcModal({
              kind: 'addPosition',
              positionId: calcModal.positionId,
              productName: calcModal.productName,
              calculationId: calcModal.calculationId,
              calculationName: calcModal.calculationName,
              calculationDescription: calcModal.calculationDescription
            })
          }
          onEditPosition={(position) =>
            setCalcModal({
              kind: 'editPosition',
              positionId: calcModal.positionId,
              productName: calcModal.productName,
              calculationId: calcModal.calculationId,
              calculationName: calcModal.calculationName,
              calculationDescription: calcModal.calculationDescription,
              editPosition: position
            })
          }
          onMetadataSaved={({ name, description }) => {
            // Po zapisie metadanych odswiezamy state, zeby tytul modala i ewentualny
            // ponowny otwart pokazywaly aktualne wartosci.
            if (calcModal.kind === 'positions') {
              setCalcModal({
                ...calcModal,
                calculationName: name,
                calculationDescription: description
              });
            }
            // Inwalidacja danych oferty (kalkulacja moze byc czytana z innych miejsc).
            invalidate();
          }}
          onPositionsChanged={invalidate}
          onCalculationDeleted={() => {
            // Po usunieciu kalkulacji: zamykamy modal i odswiezamy dane oferty
            // (ceny pozycji oferty wracaja do bazowych — backend rekalkuluje).
            setCalcModal({ kind: 'closed' });
            invalidate();
          }}
          clientId={clientId}
          offerId={offerId}
          calculationId={calcModal.calculationId}
          calculationName={calcModal.calculationName}
          calculationDescription={calcModal.calculationDescription}
        />
      )}

      {(calcModal.kind === 'addPosition' || calcModal.kind === 'editPosition') && (
        <AddCalculationPositionModal
          open
          onClose={() =>
            setCalcModal({
              kind: 'positions',
              positionId: calcModal.positionId,
              productName: calcModal.productName,
              calculationId: calcModal.calculationId,
              calculationName: calcModal.calculationName,
              calculationDescription: calcModal.calculationDescription
            })
          }
          onSaved={() => {
            queryClient.invalidateQueries(
              getSalesCrmOfferCalculationPositionsQueryKey(calcModal.calculationId)
            );
            // Zmiana pozycji kalkulacji wplywa na cene bazowa pozycji oferty (recalc
            // przez RecalculateOfferVersionViaCartService po stronie backendu), wiec
            // odswiezamy cale drzewo zapytan oferty: pozycje, totale i metadane.
            invalidate();
            setCalcModal({
              kind: 'positions',
              positionId: calcModal.positionId,
              productName: calcModal.productName,
              calculationId: calcModal.calculationId,
              calculationName: calcModal.calculationName,
              calculationDescription: calcModal.calculationDescription
            });
          }}
          clientId={clientId}
          offerId={offerId}
          calculationId={calcModal.calculationId}
          editPosition={
            calcModal.kind === 'editPosition' ? calcModal.editPosition : null
          }
        />
      )}

      {editingUnitPositionId !== null && (() => {
        const pos = positions.find((p) => p.id === editingUnitPositionId);
        if (!pos) return null;
        return (
          <EditOfferPositionUnitModal
            open
            clientId={clientId}
            offerId={offerId}
            position={{
              id: pos.id,
              productId: pos.productId ?? 0,
              productUnitId: pos.productUnitId,
              name: pos.name,
              symbol: pos.symbol,
              imageUrl: pos.imageUrl,
              unit: pos.unit
            }}
            onClose={() => setEditingUnitPositionId(null)}
            onUpdated={invalidate}
          />
        );
      })()}
    </div>
  );
};

export default TraderClientOfferProductsContainer;
